import { XInterval } from "@/types/breeze/ChartConfiguration";

// ISO dates → short form; HTML-bearing strings → stripped + truncated; everything else passes through.
export function formatAxisValue(value: unknown): string {
  if (value == null) return "";
  const str = String(value);
  const hasTime = /^\d{4}-\d{2}-\d{2}T/.test(str);
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(str);
  if (hasTime || dateOnly) {
    const d = new Date(str);
    if (!Number.isNaN(d.getTime())) {
      return hasTime
        ? d.toLocaleString(undefined, {
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })
        : d.toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
            year: "numeric",
          });
    }
  }
  return truncate(stripHtml(str), 80);
}

// Interval-aware tick formatter — matches the granularity the server grouped by
// so a monthly chart reads "Mar 2026" not "1 Mar, 00:00".
const INTERVAL_FORMATTERS: Record<XInterval, (d: Date) => string> = {
  hour: (d) => d.toLocaleString(undefined, {
    month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
  }),
  day: (d) => d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }),
  week: (d) => `w/c ${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`,
  month: (d) => d.toLocaleDateString(undefined, { month: "short", year: "numeric" }),
  quarter: (d) => `Q${Math.floor(d.getMonth() / 3) + 1} ${d.getFullYear()}`,
  year: (d) => String(d.getFullYear()),
};

export function makeIntervalFormatter(interval?: XInterval | string | null): (v: unknown) => string {
  if (!interval) return formatAxisValue;
  const formatter = INTERVAL_FORMATTERS[interval as XInterval];
  if (!formatter) return formatAxisValue;
  return (value: unknown) => {
    if (value == null) return "";
    const d = new Date(String(value));
    return Number.isNaN(d.getTime()) ? formatAxisValue(value) : formatter(d);
  };
}

// Categorical fields can hold rich text; recharts renders raw HTML as markup if we don't strip.
function stripHtml(s: string): string {
  return s
    .replace(/<br\s*\/?>(\s*)/gi, " ")
    .replace(/<\/p>\s*<p[^>]*>/gi, " ")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();
}

function truncate(s: string, max: number): string {
  return s.length <= max ? s : s.slice(0, max - 1).trimEnd() + "…";
}
